import type { Aabb } from "@townsy/contracts";

/**
 * Uniform-grid spatial index over axis-aligned rectangles (world v3, ADR-0008: the bigger city must
 * not make every movement step scan every collider). Pure and deterministic:
 *  - a query returns exactly the rectangles that overlap the query rectangle (touching counts), in
 *    their ORIGINAL ORDER, so code that walks the result behaves exactly like code that walks the
 *    whole list and skips the far ones (collision.test.ts / spatialIndex.test.ts prove it);
 *  - the index never changes after it is built: rebuild it when the boxes change (owned homes, yard
 *    items, decor). The source of truth stays the box list (streetColliders + yard colliders...).
 */
export const DEFAULT_INDEX_CELL_SIZE = 8;
/** A rectangle covering more cells than this is kept in a short "large" list instead of the grid. */
const MAX_CELLS_PER_RECT = 256;
const OFFSET = 1 << 15;
const STRIDE = 1 << 16;

function cellKey(ix: number, iz: number): number {
  return (ix + OFFSET) * STRIDE + (iz + OFFSET);
}

function overlapsClosed(r: Aabb, minX: number, minZ: number, maxX: number, maxZ: number): boolean {
  return r.minX <= maxX && r.maxX >= minX && r.minZ <= maxZ && r.maxZ >= minZ;
}

/** Grid of rectangle indices (internal building block of the collider and door indexes). */
export class RectGrid {
  readonly cellSize: number;
  private readonly rects: readonly Aabb[];
  private readonly cells = new Map<number, number[]>();
  private readonly large: number[] = [];
  private readonly stamp: Uint32Array;
  private generation = 0;

  constructor(rects: readonly Aabb[], cellSize = DEFAULT_INDEX_CELL_SIZE) {
    if (!(cellSize > 0) || !Number.isFinite(cellSize)) throw new Error("cell size must be a positive number");
    this.cellSize = cellSize;
    this.rects = rects;
    this.stamp = new Uint32Array(rects.length);
    rects.forEach((r, i) => {
      if (![r.minX, r.minZ, r.maxX, r.maxZ].every(Number.isFinite)) throw new Error(`rect ${i} is not finite`);
      const x0 = Math.floor(r.minX / cellSize);
      const x1 = Math.floor(r.maxX / cellSize);
      const z0 = Math.floor(r.minZ / cellSize);
      const z1 = Math.floor(r.maxZ / cellSize);
      if ((x1 - x0 + 1) * (z1 - z0 + 1) > MAX_CELLS_PER_RECT) {
        this.large.push(i);
        return;
      }
      for (let ix = x0; ix <= x1; ix++) {
        for (let iz = z0; iz <= z1; iz++) {
          const k = cellKey(ix, iz);
          const list = this.cells.get(k);
          if (list) list.push(i);
          else this.cells.set(k, [i]);
        }
      }
    });
  }

  get size(): number {
    return this.rects.length;
  }

  /** Indices (ascending) of the rectangles overlapping [minX, maxX] x [minZ, maxZ] (touching counts). */
  queryIndices(minX: number, minZ: number, maxX: number, maxZ: number): number[] {
    const out: number[] = [];
    if (!(minX <= maxX) || !(minZ <= maxZ)) return out;
    this.generation = (this.generation + 1) >>> 0;
    if (this.generation === 0) {
      this.stamp.fill(0);
      this.generation = 1;
    }
    const g = this.generation;
    const cs = this.cellSize;
    const x0 = Math.floor(minX / cs);
    const x1 = Math.floor(maxX / cs);
    const z0 = Math.floor(minZ / cs);
    const z1 = Math.floor(maxZ / cs);
    if ((x1 - x0 + 1) * (z1 - z0 + 1) > MAX_CELLS_PER_RECT * 4) {
      // A huge query: scanning the list is cheaper than visiting every cell.
      this.rects.forEach((r, i) => {
        if (overlapsClosed(r, minX, minZ, maxX, maxZ)) out.push(i);
      });
      return out;
    }
    for (let ix = x0; ix <= x1; ix++) {
      for (let iz = z0; iz <= z1; iz++) {
        const list = this.cells.get(cellKey(ix, iz));
        if (!list) continue;
        for (const i of list) {
          if (this.stamp[i] === g) continue;
          this.stamp[i] = g;
          if (overlapsClosed(this.rects[i]!, minX, minZ, maxX, maxZ)) out.push(i);
        }
      }
    }
    for (const i of this.large) if (overlapsClosed(this.rects[i]!, minX, minZ, maxX, maxZ)) out.push(i);
    if (out.length > 1) out.sort((a, b) => a - b);
    return out;
  }
}

/**
 * A collider spatial index (buildColliderIndex). Every function that takes colliders as
 * `readonly Aabb[]` also takes a ColliderIndex (ColliderSet) and returns exactly the same result,
 * only faster on a big street: stepMotion, moveWithSliding, pointInsideAny, pickFreeSpawn, and the
 * cars' DriveEnv.colliders (stepVehicle, isPoseValid).
 */
export interface ColliderIndex {
  readonly kind: "collider_index";
  /** The indexed boxes (the source list, not copied: do not mutate it after building). */
  readonly boxes: readonly Aabb[];
  readonly cellSize: number;
  /** Boxes overlapping `rect` (touching counts), in their original order. */
  query(rect: Aabb): Aabb[];
}

/** Colliders as a plain list (scanned in full) or as an index (queried). */
export type ColliderSet = readonly Aabb[] | ColliderIndex;

export function buildColliderIndex(boxes: readonly Aabb[], cellSize = DEFAULT_INDEX_CELL_SIZE): ColliderIndex {
  const grid = new RectGrid(boxes, cellSize);
  return {
    kind: "collider_index",
    boxes,
    cellSize,
    query(rect: Aabb): Aabb[] {
      return grid.queryIndices(rect.minX, rect.minZ, rect.maxX, rect.maxZ).map((i) => boxes[i]!);
    },
  };
}

/** Boxes of an index overlapping `rect` (touching counts), in their original order. */
export function queryColliderIndex(index: ColliderIndex, rect: Aabb): Aabb[] {
  return index.query(rect);
}

export function isColliderIndex(set: ColliderSet): set is ColliderIndex {
  return (set as Partial<ColliderIndex>).kind === "collider_index";
}

/** Every box of a collider set (the list itself, or the boxes an index was built from). */
export function colliderList(set: ColliderSet): readonly Aabb[] {
  return isColliderIndex(set) ? set.boxes : set;
}

/**
 * Candidate boxes for something inside [minX, maxX] x [minZ, maxZ]: for a list, the list itself
 * (callers test every box, as before); for an index, only the boxes overlapping the rectangle.
 */
export function collidersNear(set: ColliderSet, minX: number, minZ: number, maxX: number, maxZ: number): readonly Aabb[] {
  return isColliderIndex(set) ? set.query({ minX, minZ, maxX, maxZ }) : set;
}
